/**
 * Вход через Telegram Mini App.
 *
 * Основной вход в приложение: клиент отдаёт подписанную `initData`, сервер
 * проверяет подпись — экранов логина нет (раздел 3). В обычном Safari
 * `window.Telegram` не появляется, и приложение работает как PWA с токеном,
 * полученным по magic-link из бота.
 */

import { api } from '../api/client'
import type { AuthResult } from '../api/types'
import { hasToken, setToken } from './session'

interface TelegramWebApp {
  initData: string
  ready(): void
  expand(): void
  /** Bot API 7.7+. Без него свайп вниз закрывает окно посреди подхода. */
  disableVerticalSwipes?(): void
  setHeaderColor?(color: string): void
  setBackgroundColor?(color: string): void
  colorScheme?: string
  version?: string
  HapticFeedback?: {
    impactOccurred(style: 'light' | 'medium' | 'heavy'): void
    notificationOccurred(type: 'error' | 'success' | 'warning'): void
  }
}

declare global {
  interface Window {
    Telegram?: { WebApp?: TelegramWebApp }
  }
}

/** Адрес официального SDK. Другого источника у Mini App нет. */
const SDK_URL = 'https://telegram.org/js/telegram-web-app.js'

/**
 * Сколько ждём SDK, прежде чем запускаться без него.
 *
 * Внутри Telegram домен заведомо доступен — клиент сам с ним говорит, — и
 *четыре секунды там не тратятся никогда. Ограничение нужно для другого случая:
 * сеть пропускает запрос, но не отвечает на него. Без предела приложение
 * висело бы вечно, и человек видел бы чёрный экран.
 */
const SDK_TIMEOUT_MS = 4000

export function telegram(): TelegramWebApp | null {
  return window.Telegram?.WebApp ?? null
}

/**
 * Похоже ли, что приложение открыто как Mini App.
 *
 * Спросить об этом сам SDK нельзя — его ещё нет, а грузить его в обычном
 * Safari незачем: там он не создаст ничего, зато сможет подвесить запуск.
 * Поэтому смотрим на признаки, которые Telegram оставляет ДО загрузки SDK:
 *
 *   - мост вебвью `TelegramWebviewProxy` (iOS и Android);
 *   - параметры `tgWebApp*`, которые Telegram дописывает в адрес;
 *   - разобранные параметры запуска в sessionStorage — их кладёт сам SDK,
 *     и они переживают переход по ссылке внутри Mini App, где адрес уже
 *     чистый.
 */
export function looksLikeTelegram(): boolean {
  if (window.Telegram?.WebApp) return true
  if ('TelegramWebviewProxy' in window) return true
  if (`${window.location.hash}${window.location.search}`.includes('tgWebApp')) return true
  try {
    return window.sessionStorage.getItem('__telegram__initParams') !== null
  } catch {
    // Приватный режим может запретить sessionStorage — это не ответ «да».
    return false
  }
}

/**
 * Подгрузить SDK Telegram, если он вообще нужен.
 *
 * Возвращает `true`, когда `window.Telegram.WebApp` появился. Вне Telegram
 * отвечает `false` немедленно и в сеть не ходит вовсе.
 *
 * Ошибка и молчание сети здесь равнозначны и обе НЕ являются сбоем
 * приложения: без SDK работает путь PWA со входом по ссылке из бота.
 */
export function loadTelegramSdk(timeoutMs: number = SDK_TIMEOUT_MS): Promise<boolean> {
  if (window.Telegram?.WebApp) return Promise.resolve(true)
  if (!looksLikeTelegram()) return Promise.resolve(false)

  return new Promise((resolve) => {
    let settled = false
    const finish = (ok: boolean) => {
      if (settled) return
      settled = true
      resolve(ok)
    }

    const timer = window.setTimeout(() => finish(false), timeoutMs)
    const done = (ok: boolean) => {
      window.clearTimeout(timer)
      finish(ok)
    }

    const script = document.createElement('script')
    script.src = SDK_URL
    script.async = true
    script.onload = () => done(Boolean(window.Telegram?.WebApp))
    script.onerror = () => done(false)
    document.head.appendChild(script)
  })
}

export function isMiniApp(): boolean {
  const app = telegram()
  return Boolean(app && app.initData)
}

/**
 * Подготовить окно Telegram к использованию в зале.
 *
 * `disableVerticalSwipes` — прямая мера против риска из раздела 13: в
 * вебвью на iOS свайп вниз закрывает окно, а закрыть его посреди записи
 * подхода означает потерять подход. Метод появился в Bot API 7.7, на старых
 * клиентах его просто нет — вызываем через опциональную цепочку.
 */
export function prepareViewport(): void {
  const app = telegram()
  if (!app) return
  app.ready()
  app.expand()
  app.disableVerticalSwipes?.()
  app.setHeaderColor?.('#0f1115')
  app.setBackgroundColor?.('#0f1115')
}

/**
 * Попробовать войти тем, что осталось на устройстве помимо хранилища.
 *
 * Сессия едет на двух носителях: токен в localStorage и кука, выставленная
 * сервером. WebKit чистит localStorage после семи дней без захода на сайт, а
 * куку — нет. Поэтому пустое хранилище ещё не значит «не входил»: спрашиваем
 * сервер, и если кука жива, он вернёт свежий токен заголовком, а клиент
 * положит его обратно (`api/client.ts`).
 *
 * Запрос идёт только когда токена НЕТ. Приложение офлайн-первое, и ставить
 * запуск в зависимость от сети там, где локальный токен уже есть, нельзя:
 * человек в подвале зала должен открыть тренировку, а не ждать ответа.
 *
 * Отказ сети — это не «не залогинен», а «неизвестно». Но делать с этим на
 * пустом хранилище всё равно нечего: показать тренировку не из чего.
 */
async function sessionFromCookie(): Promise<boolean> {
  try {
    await api.get<unknown>('/auth/me', { anonymous: true })
    return hasToken()
  } catch {
    return false
  }
}

/** Короткая вибрация: подтверждение записи подхода без взгляда на экран. */
export function haptic(kind: 'light' | 'medium' | 'heavy' = 'light'): void {
  telegram()?.HapticFeedback?.impactOccurred(kind)
}

/**
 * Получить сессию.
 *
 * Возвращает true, если токен есть или удалось войти. Токен, полученный
 * раньше, не перевыпускается на каждый запуск: `initData` обновляется при
 * каждом открытии, но лишний обмен — лишняя задержка перед первым экраном.
 */
export async function ensureSession(): Promise<boolean> {
  if (hasToken()) return true

  const app = telegram()
  if (!app?.initData) return sessionFromCookie()

  const result = await api.post<AuthResult>(
    '/auth/telegram',
    { init_data: app.initData },
    { anonymous: true },
  )
  setToken(result.token)
  return true
}
